import { useRunSustainabilitySyncJob } from "@api/sustainabilityApi";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import { Alert, Button, CircularProgress, Snackbar } from "@mui/material";
import { observer } from "mobx-react";
import { useState } from "react";
import { ProgressIndicatorType } from "@/types/ProgressIndicator";

const RunJobButton = observer(() => {
    const appStore = useAppStore();
    const { mutate: useRunSustainabilityMutate, isPending } = useRunSustainabilitySyncJob();

    const [alert, setAlert] = useState<{
        type: "success" | "error" | "info";
        message: string;
    } | null>(null);

    const syncInProgress = appStore.sustainabilitySyncInProgress;

    const handleRunJob = async () => {
        if (isPending || syncInProgress) return;

        setAlert(null);

        useRunSustainabilityMutate(undefined, {
            onSuccess: () => {
                // Show start message inline (same place as progress message)
                appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SustainabilitySync, true);
                appStore.setSustainabilitySyncInProgress(i18n.t("app.sustainability-sync-event.start.message"));
                // After delay, switch to progress message in the same spot
                const progressDelayMs = 4500;
                setTimeout(() => {
                    appStore.setSustainabilitySyncInProgress(i18n.t("app.sustainability-sync-event.in-progress.message"));
                }, progressDelayMs);
            },
            onError: (err) => {
                setAlert({
                    type: "error",
                    message: err.message,
                });
            }
        });
    };

    return (
        <>
            <Button
                variant="contained"
                size="small"
                onClick={handleRunJob}
                disabled={isPending || syncInProgress}
            >
                {isPending ? (
                    <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Starting…
                    </>
                ) : (
                    "Sync SDA"
                )}
            </Button>

            {alert && (
                <Snackbar
                    open
                    autoHideDuration={4000}
                    onClose={() => setAlert(null)}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert
                        severity={alert.type}
                        onClose={() => setAlert(null)}
                        variant="filled"
                    >
                        {alert.message}
                    </Alert>
                </Snackbar>
            )}
        </>
    );
});

export default RunJobButton;
